
/* eslint-disable max-lines */
import { ClientFilterObject, WhereFilter } from '@common/filter.decorator';
import { Component, Input, ViewChild, inject } from '@angular/core';
import { IonContent, ModalOptions } from '@ionic/angular';

import { filter, lastValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ActiveFilterChip } from 'src/app/@shared/active-filter-chips/active-filter-chips.component';
import {
  BaseListFilterPage,
  CONTAIN_FILTER_SEPARATOR,
} from 'src/app/@shared/base-list-filter-page';
import { FilterPage, FilterResult } from 'src/app/filter/filter.page';

import { AuthService } from 'src/app/auth/@shared/auth.service';
import { FormMainType } from '@lib/types';
import { NavigationEnd } from '@angular/router';
import { OrganizationFilterForm, PublicFilterForm } from 'src/app/@shared/form/form.types';
import {
  OrganizationFilterResultForWhere,
  getOrganizationFilterResultByQueryParams,
  getOrganizationFilterResultByWhereFilter,
  getOrganizationWhereFilterByFilterResult,
  normalizeOrganizationFilterResult,
} from 'src/app/@shared/organization-filter.adapter';

import {
  FORM_FIELD_SELECT_OPTIONS_SUB_TYPE_FORM_BY_ID,
  OrganizationSubTypeOption,
} from 'src/app/@shared/form/form-field-config';
import {
  LOCATION_SEARCH_KEY,
  MULTI_FIELD_SEARCH_KEY,
  ONLINE_OFFERS_SEARCH_KEY,
} from '@common/search-filter.constants';
import {
  ORGANIZATION_FILTER_FORM,
  PUBLIC_ORGANIZATION_FILTER_FORM,
} from 'src/app/@shared/form/form-config';
import { OrganizationModel, OrganizationSource } from 'src/app/organization/@shared/organization.model';
import { OrganizationService } from 'src/app/organization/@shared/organization.service';
import { OrganizationSubType, OrganizationSubTypeForm } from '@common/entities';
import { PUBLIC_LIST_SUB_TYPES } from 'src/app/organization/@shared/organization-route-config';
import {
  SUB_TYPE_GATED_ORGANIZATION_FILTER_FIELDS,
  isInternalOrganizationFilterFieldAvailable,
} from 'src/app/@shared/organization-subtype-gated-filter-fields';
import { TopicService } from 'src/app/topic/@shared/topic.service';
import { getKeysByFormConfig } from 'src/app/@shared/form/form-helper';
import { getOrganizationSubTypeOptionTranslationKey } from 'src/app/@shared/organization-sub-type-translation';
import { isPublicWebAppBuild } from 'src/app/@shared/is-public-web-app';
import {
  PUBLIC_ORGANIZATION_DEEP_LINK_FILTERS,
  publicFilterFieldsConfig,
} from '@api/organization/@shared/public-fields.config';
import { transformToUpperSnakeCase } from 'src/app/@shared/utils/to-upper-snake-case';

const SKELETON_ITEM_COUNT = 30;
const TOPIC_LABEL_FETCH_MIN = 20;
const TOPIC_LABEL_SKELETON_VALUE = '________';
const LOCATION_SEARCH_EXCLUDED_SUB_TYPE_FORM: OrganizationSubTypeForm = 'selfHelpAddressRa1c';

const FILTER_QUERY_PARAM_KEYS: (keyof OrganizationFilterForm)[] = getKeysByFormConfig(ORGANIZATION_FILTER_FORM);
const PUBLIC_FILTER_QUERY_PARAM_KEYS: (keyof PublicFilterForm)[] = getKeysByFormConfig(PUBLIC_ORGANIZATION_FILTER_FORM);

type OrganizationFilterKey = keyof OrganizationFilterForm | keyof PublicFilterForm;

const PUBLIC_DEEP_LINK_FILTER_QUERY_PARAM_KEYS: OrganizationFilterKey[] =
  Object.keys(PUBLIC_ORGANIZATION_DEEP_LINK_FILTERS) as OrganizationFilterKey[];

const QUERY_PARAM_ARRAY_KEYS: OrganizationFilterKey[] = [
  'qualifications',
  'media',
  'searchForms',
  'stateLevelSubdivisions',
  'subType',
  'subTypeForm',
  'targetGroup',
  'topics',
];
const FILTER_CHIP_LABELS: Partial<Record<OrganizationFilterKey, string>> = {
  addressFederalState: 'FORM.INPUT.ADDRESS_FEDERAL_STATE.LABEL',
  hasTransparencyLabel: 'FORM.INPUT.HAS_TRANSPARENCY_LABEL.LABEL',
  isPublished: 'FORM.INPUT.IS_PUBLISHED.LABEL',
  language: 'FORM.INPUT.LANGUAGE.LABEL',
  media: 'FORM.INPUT.MEDIA.LABEL',
  qualifications: 'FORM.INPUT.QUALIFICATIONS.LABEL',
  searchForms: 'FORM.INPUT.SEARCH_FORMS.LABEL',
  stateLevelSubdivisions: 'FORM.INPUT.STATE_LEVEL_SUBDIVISIONS.LABEL',
  status: 'FORM.INPUT.STATUS.LABEL',
  subType: 'FORM.INPUT.SUB_TYPE.LABEL',
  subTypeForm: 'FORM.INPUT.SUB_TYPE_FORM.LABEL',
  targetGroup: 'FORM.INPUT.TARGET_GROUP.LABEL',
  topics: 'FORM.INPUT.TOPICS.LABEL',
};
const FILTER_OPTION_TRANSLATION_PREFIXES: Partial<Record<OrganizationFilterKey, string>> = {
  language: 'FORM.INPUT.LANGUAGE.OPTIONS',
  searchForms: 'FORM.INPUT.SUB_TYPE_FORM.OPTIONS',
  stateLevelSubdivisions: 'FORM.INPUT.ADDRESS_FEDERAL_STATE.OPTIONS',
  targetGroup: 'FORM.INPUT.TARGET_GROUP.OPTIONS',
};

type OrganizationListId = OrganizationSubTypeOption | 'otherAddress';
type OrganizationModalListId = `${OrganizationListId}-modal`;
type TopicModalMeta = {
  selectedTopics?: { id: string; title: string; displayName: string }[];
};

@Component({
  selector: 'page-organization-list',
  standalone: false,
  styleUrls: ['./organization-list.page.scss'],
  templateUrl: './organization-list.page.html',
})
export class OrganizationListPage extends BaseListFilterPage<OrganizationModel, OrganizationSource> {
  @Input() public formType: FormMainType;
  @Input() public organizationSubType: OrganizationSubType;
  @Input() public organizationSubTypeForm?: OrganizationSubTypeForm;
  @Input() public isMultiSelect: boolean;
  @Input() public selectedItems: OrganizationModel[] = [];
  @Input() public disabledItemIds: string[] = [];
  @Input() public enableAllSelection: boolean = false;
  @Input() public selectedAllState: {
    excludedIds: string[];
    isAllSelected: boolean;
    whereFilter?: any;
  } | null = null;

  @ViewChild('ListContent', {
    static: false,
  }) public content: IonContent;

  public readonly pageTrackName = 'OrganizationListPage';
  public readonly pageTrackTitle = 'Adresssuche | NAKOS Adresswelt';

  public listIds: OrganizationListId[] | OrganizationModalListId[] = [];
  public items: { [key: string]: OrganizationModel[] } = {};
  public nakosItem: OrganizationModel;

  public currentListType: 'thumbnail' | 'card' = 'thumbnail';
  public totalItemsInDatabase: number;
  public activeFilterChips: ActiveFilterChip[] = [];
  public isSearchInfoPopoverOpen: boolean = false;
  public searchInfoPopoverEvent?: Event;

  public rootPath: string;
  public activatedRouteId: string;
  public selectedItemIds: string[] = [];
  public allSelectedInFilter: boolean = false;
  public excludedItemIds = new Set<string>();
  public currentSecondarySearchValue: string = '';
  public isUnauthenticatedAccess: boolean = false;
  public showLocationSearch = true;
  public isPublicWebApp = isPublicWebAppBuild();
  public multiFieldSearchPlaceholderKey = 'VIEW.ORGANIZATION_LIST.SEARCH.MULTI_FIELD_PLACEHOLDER';
  public searchInfoTitleKey: string;
  public searchInfoDescriptionKey: string;

  private organizationService = inject(OrganizationService);
  private topicService = inject(TopicService);
  private authService = inject(AuthService);
  private topicLabelById: Record<string, string> = {};
  private topicLabelLoadingIds = new Set<string>();
  private reloadInFlightById: { [key: string]: boolean } = {};
  private queuedReloadById: { [key: string]: boolean } = {};
  public get showOnlineOffersToggle(): boolean {
    return this.isPublicWebApp && !this.isModal && !this.organizationSubType;
  }

  public get showFilterButton(): boolean {
    return !this.showOnlineOffersToggle
      && (!this.isUnauthenticatedAccess || this.formFilterKeys.some((key) => {
        return key !== 'subType';
      }));
  }

  public get includeOnlineOffers(): boolean {
    const onlineOffersValue = this.queryParams[ONLINE_OFFERS_SEARCH_KEY];

    return this.isUnauthenticatedAccess
      && (onlineOffersValue === true
        || onlineOffersValue === 'true'
        || (this.showOnlineOffersToggle
          && onlineOffersValue !== false
          && onlineOffersValue !== 'false'));
  }

  private get formFilterKeys(): OrganizationFilterKey[] {
    if (!this.isUnauthenticatedAccess) {
      return FILTER_QUERY_PARAM_KEYS.filter((key) => {
        if (this.formType !== 'selfHelpAddress' && SUB_TYPE_GATED_ORGANIZATION_FILTER_FIELDS.includes(key)) {
          return false;
        }

        return isInternalOrganizationFilterFieldAvailable(key, this.organizationSubType
          ? [this.organizationSubType]
          : []);
      });
    }

    const relevantSubTypes = this.organizationSubType
      ? [this.organizationSubType]
      : PUBLIC_LIST_SUB_TYPES;

    return PUBLIC_FILTER_QUERY_PARAM_KEYS.filter((key) => {
      return relevantSubTypes.some((subType) => {
        return key === 'subType' || publicFilterFieldsConfig[subType]?.includes(key);
      });
    });
  }

  private get queryParamFilterKeys(): OrganizationFilterKey[] {
    if (!this.isUnauthenticatedAccess) {
      return this.formFilterKeys;
    }

    return Array.from(new Set<OrganizationFilterKey>([
      ...this.formFilterKeys,
      ...PUBLIC_DEEP_LINK_FILTER_QUERY_PARAM_KEYS,
    ]));
  }

  public onItemClicked(item: OrganizationModel): Promise<boolean> {
    if (this.isModal) {
      this.toggleSelectedItem(item);

      return Promise.resolve(true);
    }

    return this.navigate([item.id],
      {
        queryParams: this.queryParams,
        relativeTo: this.activatedRoute,
        state: {
          item,
        },
      },
      'root');
  }

  public onEmptyStateItemClicked(item: OrganizationModel): Promise<boolean> {
    if (this.isModal) {
      // do nothing for now when organization list is modal
      return Promise.resolve(true);
    }

    return this.navigate([item.id],
      {
        queryParams: this.queryParams,
        relativeTo: this.activatedRoute,
        state: {
          item,
        },
      },
      'root');
  }

  public dismiss(data?: any): void {
    this.modalController.dismiss(data).catch();
  }

  public submit(): void {
    const selected = this.selectedItems.slice();

    if (!this.enableAllSelection) {
      this.dismiss(selected);

      return;
    }

    const selection = {
      excludedIds: Array.from(this.excludedItemIds),
      isAllSelected: this.allSelectedInFilter,
      whereFilter: this.filters[this.currentContentId]?.where || {},
    };

    this.dismiss({
      items: selected,
      selection,
    } as any);
  }

  public toggleSelectedItem(item: OrganizationModel): void {
    if (this.allSelectedInFilter) {
      if (this.excludedItemIds.has(item.id)) {
        this.excludedItemIds.delete(item.id);
      } else {
        this.excludedItemIds.add(item.id);
      }

      this.selectedItemIds = this.items[this.currentContentId]
        .filter((currentItem) => {
          return !this.disabledItemIds.includes(currentItem.id) && !this.excludedItemIds.has(currentItem.id);
        })
        .map((currentItem) => {
          return currentItem.id;
        });
      this.selectedItems = this.items[this.currentContentId]
        .filter((currentItem) => {
          return this.selectedItemIds.includes(currentItem.id);
        });

      return;
    }

    if (!this.isMultiSelect) {
      this.selectedItems = [item];
      this.selectedItemIds = [item.id];

      return;
    }

    if (this.selectedItems.find((o) => { return o.id === item.id; })) {
      this.selectedItems = this.selectedItems.filter((o) => { return o.id !== item.id; });
    } else {
      this.selectedItems.push(item);
    }

    this.selectedItemIds = this.selectedItems.map((o) => { return o.id; });
  }

  public toggleSelectAllInFilter(): void {
    this.allSelectedInFilter = !this.allSelectedInFilter;
    this.excludedItemIds = new Set();

    if (this.allSelectedInFilter) {
      this.selectedItemIds = this.items[this.currentContentId]
        .filter((item) => {
          return !this.disabledItemIds.includes(item?.id);
        })
        .map((item) => {
          return item?.id;
        })
        .filter(Boolean);
      this.selectedItems = this.items[this.currentContentId]
        .filter((item) => {
          return this.selectedItemIds.includes(item.id);
        });
    } else {
      this.selectedItemIds = [];
      this.selectedItems = [];
    }
  }

  public openSearchInfoPopover(event: Event): void {
    this.searchInfoPopoverEvent = event;
    this.isSearchInfoPopoverOpen = true;
  }

  public removeActiveFilterChip(chipId: string, rawValue?: string): void {
    const filterKey = chipId as OrganizationFilterKey;

    if (rawValue && QUERY_PARAM_ARRAY_KEYS.includes(filterKey)) {
      const remainingValues = this.getQueryParamArray(filterKey)
        .filter((value) => {
          return value !== rawValue;
        });

      if (remainingValues.length) {
        this.queryParams[filterKey] = remainingValues.join(CONTAIN_FILTER_SEPARATOR);
      } else {
        delete this.queryParams[filterKey];
      }
    } else {
      delete this.queryParams[filterKey];
    }

    this.updateActiveFilterChips();

    if (this.isModal) {
      this.onQueryParamsUpdated(this.currentContentId);

      return;
    }

    const [route] = this.router.url.split('?');

    this.router.navigate([route], {
      queryParams: this.queryParams,
    }).catch();
  }

  public onSecondarySearchValueUpdated(event: Event): void {
    const { value } = event.target as HTMLInputElement;

    if (value === this.currentSecondarySearchValue) {
      return;
    }

    this.currentSecondarySearchValue = value;
    this.updateQueryParamsBySearchValue(value, LOCATION_SEARCH_KEY);

    if (this.content) {
      this.content.scrollToTop().catch();
    } else {
      console.error('onSecondarySearchValueUpdated: this.content is not defined for', this.currentContentId);
    }

    if (this.isModal) {
      this.onQueryParamsUpdated(this.currentContentId);
    }
  }

  public onOnlineOffersUpdated(event: Event): void {
    const includeOnlineOffers = (event as CustomEvent<{ checked?: boolean }>).detail?.checked === true;

    if (includeOnlineOffers === this.includeOnlineOffers) {
      return;
    }

    this.updateQueryParamsBySearchValue(includeOnlineOffers ? 'true' : 'false', ONLINE_OFFERS_SEARCH_KEY);

    if (this.content) {
      this.content.scrollToTop().catch();
    }
  }

  public async onFilterButtonClicked(): Promise<void> {
    const options: ModalOptions = {
      canDismiss: false,
      component: FilterPage<OrganizationSource>,
      componentProps: {
        appliedWhereFilter: this.getAppliedWhereFilterFromQueryParams(),
        formId: this.isUnauthenticatedAccess
          ? 'filterPublicOrganization'
          : 'filterOrganization',
        formType: this.formType,
        listSubId: this.organizationSubType || 'all',
        listSubTypeForm: this.organizationSubTypeForm,
        locationSearchActive: !!this.getNormalizedSearchValue(this.queryParams[LOCATION_SEARCH_KEY]),
        presentingElement: this.content,
        topicLabelById: this.topicLabelById,
      },
      cssClass: 'proto-modal',
    };

    const { data, role } = await this.presentModal(options);

    if (role === 'cancel') {
      return;
    }

    const { selectedTopics = [] } = (data || {}) as FilterResult<OrganizationSource> & TopicModalMeta;

    for (const topic of selectedTopics) {
      if (topic?.id && topic.title) {
        this.topicLabelById[topic.id] = topic.title;
      }
    }

    const normalizedFilterResult
      = normalizeOrganizationFilterResult((data || {}) as unknown as OrganizationFilterResultForWhere,
        this.isUnauthenticatedAccess
          ? 'filterPublicOrganization'
          : 'filterOrganization');

    this.onFilterUpdated(this.currentContentId, normalizedFilterResult as FilterResult<OrganizationSource>, this.formFilterKeys);
  }

  // override base-list.page ngOnInit
  public ngOnInit(): void {
    this.isUnauthenticatedAccess = !this.isModal && !this.authService.isAuthenticated() && this.router.url.includes('/main/organizations/');

    if (this.isModal) {
      this.selectedItemIds = this.selectedItems.map((o) => { return o.id; });

      if (this.enableAllSelection && this.selectedAllState?.isAllSelected) {
        this.allSelectedInFilter = true;
        this.excludedItemIds = new Set(this.selectedAllState.excludedIds || []);
      }
    } else {
      const { formType, organizationSubType } = this.activatedRoute.snapshot.params as {
        formType: FormMainType;
        organizationSubType?: OrganizationSubTypeOption;
      };

      this.rootPath = this.getRootPath(this.router.url, organizationSubType);
      this.activatedRouteId = this.getDetailIdFromUrl(this.router.url, organizationSubType);

      this.formType = formType;
      this.organizationSubType = organizationSubType === 'all'
        ? null
        : organizationSubType;
    }

    const searchInfoKey = this.organizationSubType
      ? transformToUpperSnakeCase(this.organizationSubType)
      : 'ALL';
    const searchInfoTranslationPrefix = `VIEW.ORGANIZATION_LIST.SEARCH_INFO.${searchInfoKey}`;

    this.searchInfoTitleKey = `${searchInfoTranslationPrefix}.TITLE`;
    this.searchInfoDescriptionKey = `${searchInfoTranslationPrefix}.DESCRIPTION`;

    this.updateShowLocationSearch();

    if (this.organizationSubType === 'selfHelpAddressRa') {
      this.multiFieldSearchPlaceholderKey = 'VIEW.ORGANIZATION_LIST.SEARCH.NAME_PLACEHOLDER';
    }

    const baseListId: OrganizationListId = (this.organizationSubType || this.formType) as OrganizationListId;

    this.listIds = this.isModal
      ? [`${baseListId}-modal`]
      : [baseListId];
    [this.currentContentId] = this.listIds;

    if (this.shouldSeedQueryParamsFromPersistedSelectionFilter()) {
      this.seedQueryParamsFromPersistedSelectionFilter();
    }

    this.initList(this.currentContentId as OrganizationListId | OrganizationModalListId);
  }

  protected subscribeToContent(): void {
    if (!this.isModal) {
      // observe detail route changes
      this.router.events
        .pipe(takeUntil(this.ngUnsubscribe),
          filter((event) => { return event instanceof NavigationEnd; }))
        .subscribe((ev: NavigationEnd) => {
          if (ev instanceof NavigationEnd && ev.url) {
            this.activatedRouteId = this.getDetailIdFromUrl(this.router.url, this.organizationSubType || 'all');
          }
        });

      // observe search and filter changes
      //
      // queryParams (sanitized) should always reflect applied filter
      // currently we only do this for our where filter
      // params like order or offset are not part of query params yet

      this.activatedRoute.queryParams.subscribe((queryParams) => {
        this.queryParams = {
          ...queryParams,
        };

        this.onQueryParamsUpdated(this.currentContentId);
      });
    }

    this.subscribeToCurrentList(this.currentContentId);
  }

  protected hasMoreItems(id: string): boolean {
    return this.organizationService.hasMoreItems(id);
  }

  protected async reloadContent(id: string): Promise<void> {
    if (this.reloadInFlightById[id]) {
      this.queuedReloadById[id] = true;

      return;
    }

    this.reloadInFlightById[id] = true;

    try {
      this.setFilter(id, {
        skip: 0,
      });
      await lastValueFrom(this.organizationService.loadContent(id, {
        filter: this.filters[id],
        type: 'list',
        withoutAuthentication: this.isUnauthenticatedAccess,
      }));
    } catch (error) {
      this.onRequestFailed(id, error);
    } finally {
      this.reloadInFlightById[id] = false;

      if (this.queuedReloadById[id]) {
        this.queuedReloadById[id] = false;
        await this.reloadContent(id);
      }
    }
  }

  protected async loadNextItems(id: string): Promise<void> {
    try {
      this.setFilter(id, {
        skip: this.items[id].length,
      });
      await lastValueFrom(this.organizationService.loadContent(id, {
        filter: this.filters[id],
        type: 'list',
        withoutAuthentication: this.isUnauthenticatedAccess,
      }));
    } catch (error) {
      this.onRequestFailed(id, error);
    }
  }

  protected initList(id: OrganizationListId | OrganizationModalListId): void {
    this.viewStates[id] = this.getInitialViewStates(false);
    this.items[id] = Array(SKELETON_ITEM_COUNT).fill(null);
  }

  // eslint-disable-next-line max-lines-per-function,max-statements
  protected onQueryParamsUpdated(id: string): void {
    const excludeIndicatorKeys: OrganizationFilterKey[] = [];
    const formId = this.isUnauthenticatedAccess
      ? 'filterPublicOrganization'
      : 'filterOrganization';

    this.sanitizeLocationSearchSubTypeForm();
    this.updateShowLocationSearch();

    const multiFieldSearchValue = this.getNormalizedSearchValue(this.queryParams[MULTI_FIELD_SEARCH_KEY]);
    const normalizedFilterResult = getOrganizationFilterResultByQueryParams(this.queryParams,
      this.queryParamFilterKeys as string[],
      formId);

    if (!this.shouldShowTransparencyLabelFilter(normalizedFilterResult.subType as OrganizationSubType[] || [],
      normalizedFilterResult.subTypeForm as OrganizationSubTypeForm[] || [])) {
      delete normalizedFilterResult.hasTransparencyLabel;
    }

    const where = getOrganizationWhereFilterByFilterResult(normalizedFilterResult, formId);

    this.currentSecondarySearchValue = this.showLocationSearch
      ? this.getNormalizedSearchValue(this.queryParams[LOCATION_SEARCH_KEY])
      : '';

    if (this.currentSecondarySearchValue) {
      Object.assign(where, {
        [LOCATION_SEARCH_KEY]: this.currentSecondarySearchValue,
      } as WhereFilter<OrganizationSource>);
    }

    const { includeOnlineOffers } = this;

    if ((this.showOnlineOffersToggle || includeOnlineOffers)
      && (!includeOnlineOffers || this.currentSecondarySearchValue)) {
      Object.assign(where, {
        [ONLINE_OFFERS_SEARCH_KEY]: includeOnlineOffers,
      } as WhereFilter<OrganizationSource>);
    }

    if (multiFieldSearchValue) {
      Object.assign(where, {
        [MULTI_FIELD_SEARCH_KEY]: multiFieldSearchValue,
      } as WhereFilter<OrganizationSource>);
    }

    // edge case for organizations because `type` (and `subType`)
    // are part of the url and not a query param,
    // although they are still required for proper filtering
    where.type = this.formType;

    if (this.organizationSubType) {
      where.subType = this.organizationSubType;
      excludeIndicatorKeys.push('subType');
    }

    // and another special case
    // pre-filter organizations by subTypeForm
    // (e.g. for mainOffice and branchOffice we only want to show type 1a organizations)
    if (this.organizationSubTypeForm) {
      where.subTypeForm = this.organizationSubTypeForm;
      excludeIndicatorKeys.push('subTypeForm');
    }

    this.updateActiveFilterChips();

    const currentFilter: ClientFilterObject<OrganizationSource> = {
      where,
    };

    if (!multiFieldSearchValue) {
      currentFilter.order = this.getDefaultListOrder();
    }

    const indicatorFilterKeys = this.formFilterKeys
      .filter((key) => {
        return !excludeIndicatorKeys.includes(key);
      });

    this.applyFilterSettings(id, currentFilter, this.organizationService.getMetaData(id), indicatorFilterKeys);

    const selectedSubTypes = this.getQueryParamArray('subType');
    const selectedSubTypeForms = this.getQueryParamArray('subTypeForm');
    const selectedTopicIds = this.getQueryParamArray('topics');

    // Topic deep links are deliberately not represented in the public filter modal,
    // but topics remain an active filter for authenticated users.
    if (selectedSubTypes.length
      || selectedSubTypeForms.length
      || (!this.isUnauthenticatedAccess && selectedTopicIds.length)
      || typeof normalizedFilterResult.hasTransparencyLabel !== 'undefined') {
      this.filterIsActive = true;
    }

    if (includeOnlineOffers) {
      this.filterIsActive = true;
    }

    if (this.allSelectedInFilter) {
      this.selectedAllState ||= {
        excludedIds: [],
        isAllSelected: true,
      };
      this.selectedAllState.whereFilter = currentFilter.where;
    }

    this.ensureTopicLabels(selectedTopicIds).catch();
  }

  private getDefaultListOrder(): ClientFilterObject<OrganizationSource>['order'] {
    if (this.organizationSubType === 'selfHelpAddressBa') {
      // Keep this explicit caller order!
      return {
        topics: {
          title: 'ASC',
        },
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix,sort-keys
        name: 'ASC',
      } as ClientFilterObject<OrganizationSource>['order'];
    }

    return {
      name: 'ASC',
    };
  }

  private sanitizeLocationSearchSubTypeForm(): void {
    const locationSearchValue = this.getNormalizedSearchValue(this.queryParams[LOCATION_SEARCH_KEY]);
    const selectedSubTypeForms = this.getQueryParamArray('subTypeForm');

    if (!locationSearchValue
      || !selectedSubTypeForms.includes(LOCATION_SEARCH_EXCLUDED_SUB_TYPE_FORM)) {
      return;
    }

    const remainingSubTypeForms = selectedSubTypeForms.filter((subTypeForm) => {
      return subTypeForm !== LOCATION_SEARCH_EXCLUDED_SUB_TYPE_FORM;
    });

    this.updateQueryParamsBySearchValue(remainingSubTypeForms.join(CONTAIN_FILTER_SEPARATOR), 'subTypeForm');
  }

  private updateShowLocationSearch(): void {
    this.showLocationSearch = (!this.isPublicWebApp
      || !this.organizationSubType // all addresses
      || this.organizationSubType === 'selfHelpAddressRa'
      || this.organizationSubType === 'selfHelpAddressCommunity'
      || this.organizationSubType === 'selfHelpAddressShGroup'
      || this.organizationSubType === 'selfHelpAddressBa')
      && !this.getQueryParamArray('subTypeForm').includes(LOCATION_SEARCH_EXCLUDED_SUB_TYPE_FORM);
  }

  private getAppliedWhereFilterFromQueryParams(): FilterResult<OrganizationSource> {
    const filterResult = getOrganizationFilterResultByQueryParams(this.queryParams,
      this.formFilterKeys as string[],
      this.isUnauthenticatedAccess
        ? 'filterPublicOrganization'
        : 'filterOrganization') as FilterResult<OrganizationSource>;

    if (!this.shouldShowTransparencyLabelFilter(filterResult.subType as OrganizationSubType[] || [],
      filterResult.subTypeForm as OrganizationSubTypeForm[] || [])) {
      delete filterResult.hasTransparencyLabel;
    }

    return filterResult;
  }

  private shouldSeedQueryParamsFromPersistedSelectionFilter(): boolean {
    return this.isModal
      && this.enableAllSelection
      && !!this.selectedAllState?.whereFilter
      && Object.keys(this.queryParams || {}).length === 0;
  }

  private seedQueryParamsFromPersistedSelectionFilter(): void {
    const persistedSearchValue = this.getNormalizedSearchValue(this.selectedAllState?.whereFilter?.[MULTI_FIELD_SEARCH_KEY]);
    const persistedLocationSearchValue = this.getNormalizedSearchValue(this.selectedAllState?.whereFilter?.[LOCATION_SEARCH_KEY]);

    if (persistedSearchValue) {
      this.queryParams[MULTI_FIELD_SEARCH_KEY] = persistedSearchValue;
    } else {
      delete this.queryParams[MULTI_FIELD_SEARCH_KEY];
    }

    if (persistedLocationSearchValue) {
      this.queryParams[LOCATION_SEARCH_KEY] = persistedLocationSearchValue;
    } else {
      delete this.queryParams[LOCATION_SEARCH_KEY];
    }

    const filterResult = getOrganizationFilterResultByWhereFilter(this.selectedAllState?.whereFilter as any) as FilterResult<OrganizationSource>;

    for (const key of this.formFilterKeys) {
      const value = this.getQueryParamValue(filterResult[key]);

      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        this.queryParams[key as string] = value;
      } else {
        delete this.queryParams[key as string];
      }
    }
  }

  // eslint-disable-next-line max-lines-per-function
  private updateActiveFilterChips(): void {
    const chipOrder: OrganizationFilterKey[] = [
      'subType',
      'subTypeForm',
      'status',
      'topics',
      'addressFederalState',
      'isPublished',
      'hasTransparencyLabel',
      'qualifications',
      'media',
      'stateLevelSubdivisions',
      'searchForms',
      'targetGroup',
      'language',
    ];

    this.activeFilterChips = chipOrder.flatMap((id): ActiveFilterChip[] => {
      if (id === 'hasTransparencyLabel' && !this.shouldShowTransparencyLabelFilter()) {
        return [];
      }

      const rawValue = this.queryParams[id];

      if (!rawValue && rawValue !== false) {
        return [];
      }

      const values = QUERY_PARAM_ARRAY_KEYS.includes(id)
        ? this.getQueryParamArray(id)
        : [rawValue.toString()];

      if (!values.length) {
        return [];
      }

      const translatedValues = this.getTranslatedChipValues(id, values);

      if (QUERY_PARAM_ARRAY_KEYS.includes(id)) {
        return values.map((value, index) => {
          const isTopicLabelLoading = id === 'topics'
            && this.topicLabelLoadingIds.has(value)
            && !this.topicLabelById[value];

          return {
            id,
            isValueLoading: isTopicLabelLoading,
            label: FILTER_CHIP_LABELS[id] || id,
            rawValue: value,
            value: isTopicLabelLoading
              ? TOPIC_LABEL_SKELETON_VALUE
              : translatedValues[index] || value,
          };
        });
      }

      return [
        {
          id,
          label: FILTER_CHIP_LABELS[id] || id,
          value: translatedValues.join(', '),
        },
      ];
    });
  }

  private getTranslatedChipValues(id: OrganizationFilterKey, values: string[]): string[] {
    const optionTranslationPrefix = FILTER_OPTION_TRANSLATION_PREFIXES[id];

    if (optionTranslationPrefix) {
      return values.map((value) => {
        return this.translateOptionValue(optionTranslationPrefix, value);
      });
    }

    switch (id) {
      case 'subType':
        return values.map((value) => {
          const translationKey = getOrganizationSubTypeOptionTranslationKey(value, this.isPublicWebApp);
          const translated = this.translate.instant(translationKey);

          return translated === translationKey
            ? value
            : translated;
        });

      case 'subTypeForm':
        return values.map((value) => {
          return this.translateOptionValue('FORM.INPUT.SUB_TYPE_FORM.OPTIONS', value);
        });

      case 'topics':
        return values.map((value) => {
          return this.topicLabelById[value] || value;
        });

      case 'isPublished':

      case 'hasTransparencyLabel':
        return values.map((value) => {
          if (value === 'true') {
            return this.translate.instant('TERM.YES');
          }

          if (value === 'false') {
            return this.translate.instant('TERM.NO');
          }

          return value;
        });

      default:
        return values;
    }
  }

  private translateOptionValue(prefix: string, value: string): string {
    const translationKey = `${prefix}.${transformToUpperSnakeCase(value)}`;
    const translated = this.translate.instant(translationKey);

    return translated === translationKey
      ? value
      : translated;
  }

  private async ensureTopicLabels(topicIds: string[]): Promise<void> {
    const missingIds = topicIds.filter((id) => {
      return !!id && !this.topicLabelById[id];
    });

    if (!missingIds.length) {
      return;
    }

    missingIds.forEach((id) => {
      this.topicLabelLoadingIds.add(id);
    });
    this.updateActiveFilterChips();

    try {
      const topics = await lastValueFrom(this.topicService.loadContent('organization-list-topic-labels', {
        filter: {
          take: Math.max(missingIds.length, TOPIC_LABEL_FETCH_MIN),
          where: {
            id: {
              in: missingIds,
            },
          },
        },
        forceReload: true,
        type: 'list',
      } as any));

      for (const topic of topics || []) {
        this.topicLabelById[topic.id] = topic.title;
      }
    } finally {
      missingIds.forEach((id) => {
        this.topicLabelLoadingIds.delete(id);
      });

      this.updateActiveFilterChips();
    }
  }

  private subscribeToCurrentList(id: string): void {
    // when this.isModal = true
    // + just in case queryParams subscription wasn't initially triggered for any reason
    if (!this.filters[id]) {
      this.onQueryParamsUpdated(id);
    }

    this.organizationService.getContent(id, {
      filter: this.filters[id],
      // reload content if list is opened as modal -> fix modal opening to slow or showing last searched items
      forceReload: this.isModal,
      type: 'list',
      withoutAuthentication: this.isUnauthenticatedAccess,
    })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((items): void => {
        if (items) {
          // Ignore immediate cached emissions when they were loaded with a different filter.
          // This prevents briefly rendering stale items before the refetch for the current filter completes.
          if (!this.hasCurrentFilterResultInStore(id)) {
            return;
          }

          this.items[id] = items;
          this.totalItemsInDatabase = this.organizationService.getMetaData(id).totalCount;

          if (this.allSelectedInFilter) {
            this.selectedItemIds = items
              .filter((item) => {
                return !this.disabledItemIds.includes(item.id) && !this.excludedItemIds.has(item.id);
              })
              .map((item) => {
                return item.id;
              });
            this.selectedItems = items.filter((item) => {
              return this.selectedItemIds.includes(item.id);
            });
          }

          this.nakosItem = this.organizationService.getNakosOrganizationDetail();

          this.onRequestSucceeded(id);
        } else {
          const metadata = this.organizationService.getMetaData(id);

          this.onRequestFailed(id, metadata.error);
        }
      });
  }

  private hasCurrentFilterResultInStore(id: string): boolean {
    const metadata = this.organizationService.getMetaData(id);
    const currentWhereFilter = (this.filters[id]?.where || {}) as WhereFilter<OrganizationSource>;
    const appliedWhereFilter = (metadata.appliedFilter?.where || {}) as WhereFilter<OrganizationSource>;

    return this.filterHasChanged(currentWhereFilter, appliedWhereFilter) === false;
  }

  private getRootPath(route: string, subTypeOption: OrganizationSubTypeOption): string {
    const [normalizedRoute] = route.split('?');
    const subTypeIndex = route.indexOf(subTypeOption);

    if (subTypeIndex === -1) {
      console.warn('subType missing in ', route);

      return normalizedRoute;
    }

    return normalizedRoute.substring(0, subTypeIndex + subTypeOption.length);
  }

  // we cannot access :id here (nested router outlet issue), so we need a different approach
  private getDetailIdFromUrl(url: string, subType: OrganizationSubTypeOption): string {
    const [normalizedUrl] = url.split('?');
    const parts = normalizedUrl.split('/');
    const index = parts.indexOf(subType);

    if (index === -1) {
      return '';
    }

    return parts[index + 1];
  }

  private shouldShowTransparencyLabelFilter(subTypes?: OrganizationSubType[],
    subTypeForms?: OrganizationSubTypeForm[]): boolean {
    if (this.formType !== 'selfHelpAddress') {
      return false;
    }

    const selectedSubTypes = subTypes || this.getQueryParamArray('subType');
    const selectedSubTypeForms = subTypeForms || this.getQueryParamArray('subTypeForm');

    if (this.organizationSubType === 'selfHelpAddressGa') {
      return true;
    }

    const gaSubTypeForms = new Set<string>(FORM_FIELD_SELECT_OPTIONS_SUB_TYPE_FORM_BY_ID.selfHelpAddressGa);

    if (this.organizationSubTypeForm) {
      return gaSubTypeForms.has(this.organizationSubTypeForm);
    }

    if (this.organizationSubType) {
      return false;
    }

    if (!selectedSubTypes.length && !selectedSubTypeForms.length) {
      return true;
    }

    if (selectedSubTypes.includes('selfHelpAddressGa')) {
      return true;
    }

    return selectedSubTypeForms.some((subTypeForm) => {
      return gaSubTypeForms.has(subTypeForm);
    });
  }
}
